import { IInsurancePlan } from '@dataTypes/appointment.type'
import { InsurancePlan } from '@dataTypes/insurance.types'

export enum PlanType {
  PPO = 0,
  HMO = 1,
  UNKNOWN = 2,
}

export enum PlanStatus {
  INACTIVE = 0,
  ACTIVE = 1,
  UNKNOWN = 2,
}

export enum RenewalType {
  CALENDAR_YEAR = 0,
  COVERAGE_YEAR = 1,
}

export enum NetworkType {
  IN_NETWORK = 0,
  OUT_OF_NETWORK = 1,
  NETWORK_NOT_APPLICABLE = 2,
}

export enum CoverageLevel {
  FAMILY = 0,
  INDIVIDUAL = 1,
  UNKNOWN = 2,
}

export enum DataSource {
  EDI = 0,
  PAYER_PORTAL = 1,
}

export enum VisitPeriodUnit {
  YEAR = 0,
  MONTH = 1,
  DAY = 2,
  LIFETIME = 3,
  VISIT = 4,
  COVERAGE_YEAR = 5,
}

export const getPlanType = (planType: string): number => {
  switch (planType.toUpperCase()) {
    case 'PPO':
      return PlanType.PPO
    case 'HMO':
      return PlanType.HMO
    case 'UNKNOWN':
      return PlanType.UNKNOWN
    default:
      return PlanType.UNKNOWN
  }
}

export const getPlanTypeText = (planType: number): string => {
  switch (planType) {
    case PlanType.PPO:
      return 'PPO'
    case PlanType.HMO:
      return 'HMO'
    case PlanType.UNKNOWN:
      return 'Unknown'
    default:
      return 'Unknown'
  }
}

export const getPlanStatus = (planStatus: string): number => {
  switch (planStatus.toUpperCase()) {
    case 'ACTIVE':
      return PlanStatus.ACTIVE
    case 'INACTIVE':
      return PlanStatus.INACTIVE
    case 'UNKNOWN':
      return PlanStatus.UNKNOWN
    default:
      return PlanStatus.UNKNOWN
  }
}

export const getPlanStatusText = (planStatus: number): string => {
  switch (planStatus) {
    case PlanStatus.ACTIVE:
      return 'Active'
    case PlanStatus.INACTIVE:
      return 'Inactive'
    case PlanStatus.UNKNOWN:
      return 'Unknown'
    default:
      return 'Unknown'
  }
}

export const getRenewalType = (renewalType: string): number => {
  switch (renewalType.toUpperCase()) {
    case 'CALENDAR YEAR':
      return RenewalType.CALENDAR_YEAR
    case 'COVERAGE YEAR':
      return RenewalType.COVERAGE_YEAR
    default:
      return RenewalType.COVERAGE_YEAR
  }
}

export const getRenewalTypeText = (renewalType: number): string => {
  switch (renewalType) {
    case RenewalType.CALENDAR_YEAR:
      return 'Calendar Year'
    case RenewalType.COVERAGE_YEAR:
      return 'Coverage Year'
    default:
      return 'Coverage Year'
  }
}

export const getNetworkType = (networkType: string): number => {
  switch (networkType.toUpperCase()) {
    case 'INNETWORK':
      return NetworkType.IN_NETWORK
    case 'OUTOFNETWORK':
      return NetworkType.OUT_OF_NETWORK
    case 'NETWORKNOTAPPLICABLE':
      return NetworkType.NETWORK_NOT_APPLICABLE
    default:
      return NetworkType.NETWORK_NOT_APPLICABLE
  }
}

export const getNetworkTypeText = (networkType: number): string => {
  switch (networkType) {
    case NetworkType.IN_NETWORK:
      return 'In-network'
    case NetworkType.OUT_OF_NETWORK:
      return 'Out-of-network'
    case NetworkType.NETWORK_NOT_APPLICABLE:
      return 'Network not applicable'
    default:
      return 'Network not applicable'
  }
}

export const getCoverageLevel = (coverageLevel: string): number => {
  switch (coverageLevel.toUpperCase()) {
    case 'FAMILY':
      return CoverageLevel.FAMILY
    case 'INDIVIDUAL':
      return CoverageLevel.INDIVIDUAL
    case 'UNKNOWN':
      return CoverageLevel.UNKNOWN
    default:
      return CoverageLevel.UNKNOWN
  }
}

export const getCoverageLevelText = (coverageLevel: number): string => {
  switch (coverageLevel) {
    case CoverageLevel.FAMILY:
      return 'Family'
    case CoverageLevel.INDIVIDUAL:
      return 'Individual'
    case CoverageLevel.UNKNOWN:
      return 'Unknown'
    default:
      return 'Unknown'
  }
}

export const getDataSource = (dataSource: string): number => {
  switch (dataSource.toUpperCase()) {
    case 'EDI':
      return DataSource.EDI
    case 'PAYERPORTAL':
      return DataSource.PAYER_PORTAL
    default:
      return DataSource.PAYER_PORTAL
  }
}

export const getDataSourceText = (dataSource: number): string => {
  switch (dataSource) {
    case DataSource.EDI:
      return 'EDI'
    case DataSource.PAYER_PORTAL:
      return 'Payer Portal'
    default:
      return 'Payer Portal'
  }
}

export const getVisitPeriodUnit = (visitPeriodUnit: string): number => {
  switch (visitPeriodUnit.toUpperCase()) {
    case 'YEAR':
      return VisitPeriodUnit.YEAR
    case 'MONTH':
      return VisitPeriodUnit.MONTH
    case 'DAY':
      return VisitPeriodUnit.DAY
    case 'LIFETIME':
      return VisitPeriodUnit.LIFETIME
    case 'VISIT':
      return VisitPeriodUnit.VISIT
    case 'COVERAGEYEAR':
      return VisitPeriodUnit.COVERAGE_YEAR
    default:
      return VisitPeriodUnit.COVERAGE_YEAR
  }
}

export const getVisitPeriodUnitText = (visitPeriodUnit: number): string => {
  switch (visitPeriodUnit) {
    case VisitPeriodUnit.YEAR:
      return 'Year'
    case VisitPeriodUnit.MONTH:
      return 'Month'
    case VisitPeriodUnit.DAY:
      return 'Day'
    case VisitPeriodUnit.LIFETIME:
      return 'Lifetime'
    case VisitPeriodUnit.VISIT:
      return 'Visit'
    case VisitPeriodUnit.COVERAGE_YEAR:
      return 'Coverage Year'
    default:
      return 'Coverage Year'
  }
}

export const getInsuranceFieldValues = (
  procedureId: string,
  clinicFee: number,
  insurancePlanId: string,
  insurancePlans: InsurancePlan[],
  providerId: string = '',
): any => {
  let allowedAmount: number | null = null
  let writeOff: number | null = null
  let estimatedInsurancePay: number | null = null
  let estimatedPatientResp: number | null = null
  const insurancePlan = (insurancePlans || []).find(
    (insurance) => insurance.insurance_plan_id === insurancePlanId,
  ) as unknown as IInsurancePlan | undefined
  if (insurancePlan) {
    const fees = insurancePlan.fees
    if (fees) {
      const fee = fees.find((x) => x.procedure_id === procedureId)
      if (fee) {
        const providerFee =
          fee.provider_fees?.find((x: any) => x.provider_id === providerId) ??
          null
        allowedAmount = providerFee?.allowed_amount ?? fee.allowed_amount
      }
    }
    let benefits = insurancePlan.benefits
    if (!benefits) {
      benefits =
        insurancePlan.services?.flatMap((service) => service.benefits) ?? null
    }
    if (benefits) {
      const benefit = benefits.find((x) => x.procedure_id === procedureId)
      if (benefit) {
        if (clinicFee) {
          estimatedInsurancePay = clinicFee * (benefit.co_insurance / 100)
        }
        if (allowedAmount) {
          estimatedInsurancePay = allowedAmount * (benefit.co_insurance / 100)
        }
      }
    }
  }

  if (clinicFee) {
    estimatedPatientResp = clinicFee - (estimatedInsurancePay ?? 0)
  }
  if (allowedAmount) {
    estimatedPatientResp = allowedAmount - (estimatedInsurancePay ?? 0)
  }

  if (clinicFee && allowedAmount) {
    writeOff = clinicFee - allowedAmount
  }
  return {
    insurance_plan_id: insurancePlanId || null,
    clinic_fee: clinicFee || null,
    allowed_amount: allowedAmount || null,
    write_off: writeOff || null,
    estimated_insurance_pay: estimatedInsurancePay || null,
    estimated_patient_resp: estimatedPatientResp || null,
    ...(providerId ? { provider_id: providerId } : {}),
  }
}

interface Money {
  amount: number
  remaining: number
  used: number
}

interface ServiceOutput {
  category: string

  deductibles?: {
    individual?: Money
    family?: Money
  }

  maximum?: {
    individual?: Money
    family?: Money
  }

  benefits?: any[]
}

interface NetworkOutput {
  network: string
  networkType: number
  services: ServiceOutput[]
}

function prettify(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

const OUT_OF_NETWORK = 'OUT OF NETWORK'
const PLAN_WIDE_CATEGORY = 'dental_care'

function coverageLevelKey(coverageLevel: number): 'individual' | 'family' {
  return coverageLevel === 1 ? 'individual' : 'family'
}

function toMoney(record: any): Money {
  return {
    amount: record.amount,
    remaining: record.remaining,
    used: record.amount - record.remaining,
  }
}

function benefitMatchesNetwork(
  b: any,
  network: string,
  networkType: number,
): boolean {
  // 1) If the source tagged the benefit with a specific plan name, use it directly
  if (b.network) {
    return b.network === network
  }
  // 2) Otherwise fall back to network_type (the in/out hierarchy grouping)
  return b.network_type === networkType
}

function serviceMaximumAmount(s: ServiceOutput): number | null {
  return s.maximum?.individual?.amount ?? s.maximum?.family?.amount ?? null
}

function highestMaximum(n: NetworkOutput): number {
  return n.services.reduce((best, s) => {
    const amount = serviceMaximumAmount(s)
    return amount != null && amount > best ? amount : best
  }, -Infinity)
}

function totalMaximum(n: NetworkOutput): number {
  return n.services.reduce((sum, s) => sum + (serviceMaximumAmount(s) ?? 0), 0)
}

function totalIndividualDeductible(n: NetworkOutput): number {
  return n.services.reduce(
    (sum, s) => sum + (s.deductibles?.individual?.amount ?? 0),
    0,
  )
}

function compareNetworks(a: NetworkOutput, b: NetworkOutput): number {
  if (a.networkType !== b.networkType) return a.networkType - b.networkType
  const hiDiff = highestMaximum(b) - highestMaximum(a)
  if (hiDiff !== 0) return hiDiff
  const totDiff = totalMaximum(b) - totalMaximum(a)
  if (totDiff !== 0) return totDiff
  const dedDiff = totalIndividualDeductible(a) - totalIndividualDeductible(b)
  if (dedDiff !== 0) return dedDiff
  return a.network.localeCompare(b.network)
}

function comparePlanWideFirst(a: any, b: any): number {
  const aRank = a.category === PLAN_WIDE_CATEGORY ? 0 : 1
  const bRank = b.category === PLAN_WIDE_CATEGORY ? 0 : 1
  return aRank - bRank
}

function buildServiceOutput(
  service: any,
  network: string,
  networkType: number,
  includeBenefits: boolean,
): ServiceOutput {
  const serviceOutput: ServiceOutput = { category: prettify(service.category) }

  const deductibles = service.deductibles.filter(
    (d: any) => d.network === network,
  )
  if (deductibles.length) {
    serviceOutput.deductibles = {}
    for (const d of deductibles) {
      serviceOutput.deductibles[coverageLevelKey(d.coverage_level)] = toMoney(d)
    }
  }

  const maximums = service.maximums.filter((m: any) => m.network === network)
  if (maximums.length) {
    serviceOutput.maximum = {}
    for (const m of maximums) {
      serviceOutput.maximum[coverageLevelKey(m.coverage_level)] = toMoney(m)
    }
  }

  if (includeBenefits) {
    serviceOutput.benefits = service.benefits
      .filter((b: any) => benefitMatchesNetwork(b, network, networkType))
      .map((b: any) => ({
        procedureCode: b.procedure_code,
        procedureDescription: b.procedure_description,
        coPay: b.co_pay,
        coInsurance: b.co_insurance,
        ageMaximum: b.age_maximum,
        lastVisit: b.last_visit,
        visitLimit: b.visit_limit,
        visitPeriod: b.visit_period,
        visitPeriodUnit: b.visit_period_unit,
      }))
  }

  return serviceOutput
}

export function getInsurancePlanSummary(
  services: any[],
  includeBenefits: boolean = false,
): NetworkOutput[] {
  const networkTypes = new Map<string, number>()

  for (const service of services) {
    service.deductibles.forEach((d: any) => {
      if (d.network) networkTypes.set(d.network, d.network_type)
    })
    service.maximums.forEach((m: any) => {
      if (m.network) networkTypes.set(m.network, m.network_type)
    })
    service.benefits.forEach((b: any) => {
      if (b.network_type === 1 && !networkTypes.has(OUT_OF_NETWORK)) {
        networkTypes.set(OUT_OF_NETWORK, 1)
      }
    })
  }

  const orderedServices = [...services].sort(comparePlanWideFirst)

  const result: NetworkOutput[] = Array.from(networkTypes.entries()).map(
    ([network, networkType]) => ({
      network,
      networkType,
      services: orderedServices.map((service) =>
        buildServiceOutput(service, network, networkType, includeBenefits),
      ),
    }),
  )

  return result.sort(compareNetworks)
}
